import { useCallback, useEffect } from 'react';
import { useQueryClient } from 'react-query';
import { get } from '../api/fetch';
import { appendPreviewDate } from '../api/helper';
import { usePrice } from '../preload/pages/ProductPage/providers/PriceProvider';
import { ProductContext } from '../preload/pages/ProductPage/providers/ProductProvider';
import { SiteContext } from '../SiteContext';
import useContext from './useContext';

export const ARTICLE_STORE_CACHE_KEY = 'ARTICLE_STORE_CACHE_KEY';

interface IStoredArticle {
  id?: string;
  variantId?: string;
  price?: Partial<IPrice>;
}

const _store: Record<string, IStoredArticle> = {};

export default (article?: IStoredArticle) => {
  const {
    data: { searchServiceUrl },
  } = useContext(SiteContext);
  const product = useContext(ProductContext);
  const price = usePrice();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (article?.id) {
      _store[article?.id] = article;
    }

    if (product?.variantId) {
      _store[product.variantId] = {
        ...product,
        price: { price: price.price, before: price.oldPrice },
      };
    }
  }, [article, product, price]);

  const storeArticle = useCallback((storeArticle: IStoredArticle) => {
    if (storeArticle?.id) {
      _store[storeArticle.id] = storeArticle;
    }

    if (storeArticle?.variantId) {
      _store[storeArticle.variantId] = storeArticle;
    }
  }, []);

  const getArticlesAsync = useCallback((ids: string[]) => {
    const cached = [] as IStoredArticle[];
    const missing = [] as string[];
    ids.forEach((id) => {
      const cachedArticle = _store[id] || queryClient.getQueryData([ARTICLE_STORE_CACHE_KEY, id]);
      if (cachedArticle) {
        cached.push(cachedArticle);
      } else {
        missing.push(id);
      }
    });

    if (!missing.length) {
      return Promise.resolve(cached);
    }

    const url = appendPreviewDate(`${searchServiceUrl}/variants?id=${missing.join('&id=')}`);

    return get<{ variants: IProduct[] }>(url).then(({ variants }) => {
      variants.forEach((a) => queryClient.setQueryData([ARTICLE_STORE_CACHE_KEY, a.id], a));
      return cached.concat(variants);
    });
  }, []);

  return {
    getArticlesAsync,
    store: _store,
    storeArticle,
  };
};
